import { useState, useRef, useEffect, useCallback } from 'react';

interface FullAudioPlayerProps {
  audioUrl: string;
  title: string;
  duration?: string;
  image?: string;
}

export default function FullAudioPlayer({ audioUrl, title, duration = '12:34', image }: FullAudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showVolume, setShowVolume] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeed, setShowSpeed] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const volumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audio.preload = 'metadata';
    audio.volume = volume;
    audio.playbackRate = playbackRate;
    audioRef.current = audio;

    const onLoaded = () => {
      setTotalDuration(audio.duration || 0);
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };

    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);

    return () => {
      audio.pause();
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
    };
  }, [audioUrl]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [isPlaying]);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !totalDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percent * totalDuration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(percent * 100);
  }, [totalDuration]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
    volumeTimeoutRef.current = setTimeout(() => setShowVolume(false), 2000);
  }, []);

  const formatTime = (time: number) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3 mb-4">
        {image && (
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden flex-shrink-0">
            <img src={image} alt={title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm md:text-base font-bold text-foreground-900 leading-tight">{title}</h4>
          <p className="text-xs text-foreground-500 mt-0.5">{isPlaying ? 'Tocando agora' : 'Pronto para ouvir'}</p>
        </div>
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center flex-shrink-0">
          <i className="ri-headphone-line text-background-50 text-lg" />
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <button
          type="button"
          onClick={togglePlay}
          className="relative w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-all cursor-pointer flex-shrink-0 shadow-lg shadow-primary-500/30 hover:shadow-primary-500/40 hover:scale-105 active:scale-95"
          aria-label={isPlaying ? 'Pausar' : 'Tocar'}
        >
          {isPlaying && (
            <span className="absolute inset-0 rounded-full bg-primary-500 animate-ping opacity-20" />
          )}
          <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-xl`} />
        </button>

        <div className="flex-1 min-w-0">
          <div
            className="h-1.5 bg-background-300/50 rounded-full overflow-hidden cursor-pointer"
            onClick={handleProgressClick}
          >
            <div
              className="h-full bg-primary-500 rounded-full transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-[11px] text-foreground-600 tabular-nums">{formatTime(currentTime)}</span>
            <span className="text-[11px] text-foreground-500 tabular-nums">
              {totalDuration > 0 ? formatTime(totalDuration) : duration}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowSpeed(!showSpeed)}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 transition-all text-xs font-semibold cursor-pointer"
            aria-label="Velocidade de reprodução"
          >
            {playbackRate}x
          </button>
          {showSpeed && (
            <div className="absolute bottom-full left-0 mb-1 bg-background-50 border border-background-200/70 rounded-lg shadow-xl py-1 z-10">
              {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => { setPlaybackRate(rate); setShowSpeed(false); }}
                  className={`block w-full px-3 py-1.5 text-xs text-left whitespace-nowrap cursor-pointer transition-colors ${
                    playbackRate === rate ? 'text-primary-600 font-bold bg-primary-50' : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {rate}x
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowVolume(!showVolume)}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 hover:bg-background-200 transition-all text-sm cursor-pointer"
            aria-label="Volume"
          >
            <i className={`${volume === 0 ? 'ri-volume-mute-line' : volume < 0.5 ? 'ri-volume-down-line' : 'ri-volume-up-line'}`} />
          </button>
          {showVolume && (
            <div className="absolute bottom-full left-0 mb-1 bg-background-50 border border-background-200/70 rounded-lg shadow-xl p-3 z-10 w-28">
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolumeChange}
                className="w-full h-1 bg-background-300 rounded-full appearance-none cursor-pointer accent-primary-500"
              />
            </div>
          )}
        </div>

        {isPlaying && (
          <div className="flex items-end gap-[2px] h-5 flex-1 justify-end">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="w-[3px] bg-accent-500 rounded-full animate-pulse"
                style={{
                  height: `${16 + (i % 4) * 8}px`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: `${0.5 + (i % 3) * 0.2}s`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}